import { Add, Send, Visibility } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { FormTemplateLink } from '../../../types/FormTemplate';
import { UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';
import RolesPopover from '../popover/RolesPopover';
import AddFormTemplateModal from './AddFormTemplateModal';
import AppLink from '../AppLink/AppLink';
import { ROUTE_PATHS } from '../../../routes/Routes';
import FormSubmissionsModal from './FormSubmissionsModal';
import InviteSendModal from './InviteSendModal';
import { useGetFeatureFlags } from '../../../services/react-query/users/useGetFeatureFlags';

interface IFormTemplatesSectionProps {
  linkingModels: FormTemplateLink[];
  setLinkingModels: (linkingModels: FormTemplateLink[]) => void;
  collapseId: UserSettingsCollapseKeys;
  accountUuid?: string;
}

type ModalOpen = 'modify' | 'invite' | 'view' | null;

export default function FormTemplatesSection({
  linkingModels,
  setLinkingModels,
  collapseId,
  accountUuid
}: IFormTemplatesSectionProps) {
  const { data: featureFlags } = useGetFeatureFlags();

  const { disabled } = useContext(DisableContext);

  const [modalOpen, setModalOpen] = useState<ModalOpen>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplateLink | null>(null);
  const isEvent = collapseId === UserSettingsCollapseKeys.EVENT_ITEM_FORM_TEMPLATES;

  if (!featureFlags || !featureFlags['forms']) return null;

  const handleAddFormTemplate = (linkingData: FormTemplateLink) => {
    if (selectedTemplate) {
      setLinkingModels(
        _.map(linkingModels, (template) => (template.uuid === selectedTemplate.uuid ? { ...linkingData, new_record: template.new_record } : template))
      );
    } else {
      setLinkingModels([...linkingModels, { ...linkingData, new_record: true }]);
    }

    closeModal();
  };

  const handleRemoveFormTemplate = (uuid: string) => {
    setLinkingModels(_.reject(linkingModels, { uuid }));
  };

  const closeModal = () => {
    setSelectedTemplate(null);
    setModalOpen(null);
  };

  const openModal = (template: FormTemplateLink | null, newModalOpen: NonNullable<ModalOpen>) => {
    setSelectedTemplate(template);
    setModalOpen(newModalOpen);
  };

  const formTemplateTableHeaderActions = (
    <ResponsiveButton
      startIcon={<Add />}
      variant="outlined"
      disabled={disabled}
      onClick={() => openModal(null, 'modify')}
    >
      Add
    </ResponsiveButton>
  );

  const formTemplateTableRows = _.map(linkingModels, (linkingModel: FormTemplateLink) => {
    return (
      <TableRow key={linkingModel.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell><AppLink to={`/${ROUTE_PATHS.FORM_TEMPLATES}/${linkingModel.form_template_uuid}`}>{linkingModel.form_template_name}</AppLink></TableCell>
        <TableCell>{linkingModel.open_offset}</TableCell>
        <TableCell>{linkingModel.close_offset}</TableCell>
        <TableCell>
          <RolesPopover roles={linkingModel.roles_values} />
        </TableCell>
        <TableCell sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEvent && !linkingModel.new_record && (
            <>
              <IconButton onClick={() => openModal(linkingModel, 'invite')}>
                <Send />
              </IconButton>
              <IconButton onClick={() => openModal(linkingModel, 'view')}>
                <Visibility />
              </IconButton>
            </>
          )}
          {!disabled && (
            <>
              <IconButton onClick={() => openModal(linkingModel, 'modify')}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleRemoveFormTemplate(linkingModel.uuid)}>
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <AccordionSection title={isEvent ? 'Forms' : 'Form Templates'} headerElements={formTemplateTableHeaderActions} collapseId={collapseId} hasData={linkingModels.length > 0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Form Template Name</TableCell>
                <TableCell>Open Offset</TableCell>
                <TableCell>Close Offset</TableCell>
                <TableCell>Recipient Roles</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{formTemplateTableRows}</TableBody>
          </Table>
        </TableContainer>
      </AccordionSection>

      <AddFormTemplateModal
        isOpen={modalOpen == 'modify'}
        onCancel={closeModal}
        onAdd={handleAddFormTemplate}
        formTemplateLink={selectedTemplate}
      />

      <FormSubmissionsModal
        isOpen={modalOpen == 'view'}
        onClose={closeModal}
        formTemplateLink={selectedTemplate}
      />

      <InviteSendModal
        isOpen={modalOpen === 'invite'}
        onCancel={closeModal}
        formTemplateLink={selectedTemplate}
        accountUuid={accountUuid}
      />
    </>
  );
}
